import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { InviteData } from '@/types/invite';
import styles from './CharmingNaturalNavigation.module.css';

// Импорт изображений для навигационных карточек
import navigationStoryImg from '@/assets/navigation-story.jpg';
import navigationDetailsImg from '@/assets/navigation-details.jpg';
import navigationRsvpImg from '@/assets/navigation-rsvp.jpg';
import navigationLocationImg from '@/assets/navigation-location.jpg';

type CharmingNaturalNavigationProps = {
  invite: InviteData;
};

/**
 * Навигационная витрина для премиум layout "Charming Natural"
 * Grid 2x2 с 4 карточками-разделами
 */
const CharmingNaturalNavigation = memo(({ invite }: CharmingNaturalNavigationProps) => {
  const navigate = useNavigate();

  // Проверяем наличие story (опциональная секция)
  const hasStory = useMemo(() => {
    const story = invite.customFields?.story;
    if (!story) return false;
    // Проверяем разные форматы story
    if (typeof story === 'object' && story !== null) {
      const storyObj = story as { text?: string; images?: unknown[] };
      return (
        (storyObj.text && storyObj.text.trim().length > 0)
        || (Array.isArray(storyObj.images) && storyObj.images.length > 0)
      );
    }
    if (typeof story === 'string') {
      return story.trim().length > 0;
    }
    return false;
  }, [invite.customFields?.story]);

  // Функция для плавной прокрутки к секции
  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Обработчик для Location - открывает 2GIS
  const handleLocationClick = useCallback(() => {
    // Формируем URL для 2GIS с адресом
    const location = encodeURIComponent(invite.weddingLocation);
    const url = `https://2gis.ru/search/${location}`;
    window.open(url, '_blank');
  }, [invite.weddingLocation]);

  const cards = useMemo(() => {
    const cardsArray = [];

    // 1. OUR STORY - опциональная (показываем только если есть данные)
    if (hasStory) {
      cardsArray.push({
        id: 'story',
        title: 'ИСТОРИЯ',
        onClick: () => navigate(`/invite/${invite.id}/story`),
        backgroundImage: navigationStoryImg,
        backgroundClass: styles.storyBg,
      });
    }

    // 2. DETAILS - обязательная
    cardsArray.push({
      id: 'details',
      title: 'ИНФОРМАЦИЯ',
      onClick: () => scrollToSection('details'),
      backgroundImage: navigationDetailsImg,
      backgroundClass: styles.detailsBg,
    });

    // 3. RSVP - обязательная
    cardsArray.push({
      id: 'rsvp',
      title: 'RSVP',
      onClick: () => scrollToSection('rsvp'),
      backgroundImage: navigationRsvpImg,
      backgroundClass: styles.rsvpBg,
    });

    // 4. LOCATION - обязательная
    cardsArray.push({
      id: 'location',
      title: 'КАК ДОБРАТЬСЯ',
      onClick: handleLocationClick,
      backgroundImage: navigationLocationImg,
      backgroundClass: styles.locationBg,
    });

    return cardsArray;
  }, [hasStory, navigate, invite.id, scrollToSection, handleLocationClick]);

  return (
    <section className={styles.navigation}>
      <div className={styles.grid}>
        {cards.map((card, index) => (
          <motion.button
            key={card.id}
            type="button"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            onClick={card.onClick}
            className={`${styles.card} ${card.backgroundClass}`}
            style={
              card.backgroundImage
                ? {
                  backgroundImage: `url(${card.backgroundImage})`,
                }
                : undefined
            }
          >
            <div className={styles.overlay} />
            <div className={styles.content}>
              <h3 className={styles.title}>{card.title}</h3>
              <div className={styles.underline} />
            </div>
          </motion.button>
        ))}
      </div>
    </section>
  );
});

CharmingNaturalNavigation.displayName = 'CharmingNaturalNavigation';

export default CharmingNaturalNavigation;
